import { Building2, Plus, Search } from 'lucide-react'
import { useDeferredValue, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { usePagination } from '@/components/common/usePagination'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ConfirmModal } from '@/components/ui/modal'
import { HR_ORG_WRITE_PERM } from '@/features/hr/permissions'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useDeleteDepartment, useDepartments } from './api'
import { DepartmentDetailPanel } from './components/DepartmentDetailPanel'
import { DepartmentFormDialog } from './components/DepartmentFormDialog'
import { DepartmentsTable } from './components/DepartmentsTable'
import { DepartmentTree } from './components/DepartmentTree'
import type { Department } from './types'
import { toastError } from './ui'

export function DepartmentsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const remove = useDeleteDepartment()
  const all = useDepartments()
  const [q, setQ] = useState('')
  const search = useDeferredValue(q.trim().toLocaleLowerCase('tr'))
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [dialog, setDialog] = useState<{ open: boolean; department: Department | null }>({ open: false, department: null })
  const [pendingDelete, setPendingDelete] = useState<Department | null>(null)
  const canWrite = can(HR_ORG_WRITE_PERM)
  const treeDepartments = useMemo(() => all.data ?? [], [all.data])
  const filtered = useMemo(
    () =>
      treeDepartments.filter(
        (d) => !search || `${d.name} ${d.code} ${d.manager?.name ?? ''} ${d.parent?.name ?? ''}`.toLocaleLowerCase('tr').includes(search),
      ),
    [treeDepartments, search],
  )
  const { pageRows: rows, pager } = usePagination(filtered, search)
  const total = treeDepartments.length
  const selected = useMemo(() => {
    if (!selectedId) return rows[0] ?? null
    return rows.find((d) => d.id === selectedId) ?? treeDepartments.find((d) => d.id === selectedId) ?? rows[0] ?? null
  }, [rows, selectedId, treeDepartments])

  const confirmDelete = async () => {
    if (!pendingDelete) return
    try {
      await remove.mutateAsync(pendingDelete.id)
      toast.success(t('access.departments.deleted'))
      if (selectedId === pendingDelete.id) setSelectedId(null)
      setPendingDelete(null)
    } catch (e) {
      setPendingDelete(null)
      toastError(e)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.hr')}
        title={t('access.departments.title')}
        description={t('access.departments.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setDialog({ open: true, department: null })}>
              <Plus />
              {t('access.departments.create')}
            </Button>
          ) : null
        }
      />

      <div className="grid gap-4 xl:grid-cols-[240px_minmax(0,1fr)]">
        <aside className="hidden overflow-hidden rounded-2xl border bg-card p-3 xl:block">
          {all.isError ? (
            <ErrorState error={all.error} onRetry={() => void all.refetch()} />
          ) : (
            <DepartmentTree
              departments={treeDepartments}
              selectedId={selected?.id ?? null}
              onSelect={(d) => {
                setSelectedId(d.id)
                setDialog((s) => ({ ...s, open: false }))
              }}
            />
          )}
        </aside>

        <div className="space-y-4">
          <section className="overflow-hidden rounded-2xl border bg-card">
            <div className="flex flex-wrap items-center gap-2 border-b p-3">
              <div className="relative w-full max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.departments.searchPlaceholder')} aria-label={t('access.departments.searchPlaceholder')} className="pl-8" />
              </div>
              <span className="ml-auto text-xs text-muted-foreground">{t('access.departments.count', { count: filtered.length, total })}</span>
            </div>
            {all.isError ? (
              <ErrorState error={all.error} onRetry={() => void all.refetch()} />
            ) : (
              <>
                <DepartmentsTable
                  rows={rows}
                  loading={all.isLoading}
                  selectedId={selected?.id ?? null}
                  canWrite={canWrite}
                  onSelect={(d) => setSelectedId(d.id)}
                  onEdit={(d) => setDialog({ open: true, department: d })}
                  onDelete={setPendingDelete}
                />
                {!all.isLoading && filtered.length === 0 ? (
                  <EmptyState icon={Building2} title={total ? t('access.departments.noMatch') : t('access.departments.empty')} />
                ) : null}
                <Pager {...pager} />
              </>
            )}
          </section>

          {selected ? (
            <DepartmentDetailPanel
              department={selected}
              allDepartments={treeDepartments}
              canWrite={canWrite}
              onEdit={() => setDialog({ open: true, department: selected })}
              onSelectDepartment={(d) => setSelectedId(d.id)}
            />
          ) : null}
        </div>
      </div>

      <DepartmentFormDialog open={dialog.open} department={dialog.department} onOpenChange={(open) => setDialog((s) => ({ ...s, open }))} />
      <ConfirmModal
        open={!!pendingDelete}
        onOpenChange={(v) => !v && setPendingDelete(null)}
        title={t('access.departments.deleteTitle')}
        description={t('access.departments.deleteBody', { name: pendingDelete?.name ?? '' })}
        confirmLabel={t('common.delete')}
        destructive
        loading={remove.isPending}
        onConfirm={() => void confirmDelete()}
      />
    </>
  )
}
